import * as React from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import moment from 'moment';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { batch, useSelector } from 'react-redux';
import { Box, Collapse, Typography } from '@material-ui/core';
import { changeIsFetchingCredits, fetchOnlineTrainingCredits } from 'slices/onlineTrainingCredits';
import { changeIsFetchingSSOUrl } from 'slices/onlineTrainingSSO';
import { Component, componentMapper, foundationProducts, FoundationProducts } from '../../helpers/newDashboard';
import { CountryIso2Code } from '../../types/enums';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { DashboardSubscriptionDetails } from '../../components/NewDashboard/DashboardSubscriptionDetails';
import { DashboardTabs } from '../../components/NewDashboard/DashboardTabs';
import { fetchBusinessShieldCredits } from 'slices/businessShieldCredits';
import { fetchBusinessShieldSSOUrl } from 'slices/businessShieldSSO';
import { fetchMainSubscription } from '@pec/aion-ui-core/slices/mainSubscription';
import { fetchOrganizationInfo } from '@pec/aion-ui-core/slices/organizationInfo';
import { FoundationBanner } from '../../components/NewDashboard/FoundationBanner';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { ONLINE_TRAINING_STORAGE_KEY } from '../OnlineTraining/OnlineTraining';
import { OnlineTrainingDrawer } from 'components/OnlineTraining/OnlineTrainingDrawer';
import { ProductCard } from '../../components/NewDashboard/ProductCard';
import { SubscriptionAlert } from '@pec/aion-ui-components/components/SubscriptionAlert';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';
import { useAppDispatch, useTypedSelector } from '../../combineReducers';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { DISCLAIMER_NOTICE } from '../../constants/disclaimerNotice';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useState } from 'react';
import { mappedFeatureFlags } from '@pec/aion-ui-core/helpers/featureFlags';
import { OrganizationRegistrationNumberType } from '@pec/aion-ui-core/interfaces/organizationInfo';
import { ExternalVerificationAlert } from '@pec/aion-ui-components/components/ExternalVerificationAlert';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(0.5, 1)
    },
    card: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      '& > *': {
        marginBottom: theme.spacing(2)
      }
    },
    categoryContainer: {
      backgroundColor: '#e8f5e9',
      margin: 'auto',
      flexWrap: 'wrap'
    },
    cardContainer: {
      margin: 'auto',
      width: '1200px',
      marginBottom: theme.spacing(4.5),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        padding: theme.spacing(2)
      }
    },
    featuredCard: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#e8f5e9',
      margin: 'auto',
      width: '1200px',
      marginBottom: theme.spacing(4.5),
      padding: theme.spacing(5),
      borderRadius: theme.shape.borderRadius,
      flexWrap: 'wrap',
      [theme.breakpoints.down('sm')]: {
        width: '90%',
        padding: theme.spacing(2),
        flexDirection: 'column'
      }
    },
    sectionTitle: {
      marginBottom: theme.spacing(3),
      paddingLeft: theme.spacing(1),
      fontWeight: theme.typography.fontWeightBold
    },
    cardsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      marginBottom: theme.spacing(2)
    },
    disclaimer: {
      textAlign: 'center'
    },
    name: {
      paddingLeft: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      columnGap: theme.spacing(2),
      '& > svg': {
        cursor: 'pointer',
        fontSize: 30
      }
    },
    nameLink: {
      cursor: 'pointer',
      padding: theme.spacing(0.25),
      textDecoration: 'underline'
    },
    alerts: {
      '& > * + *': {
        marginTop: theme.spacing(1)
      }
    }
  })
);

enum CardCategoriesEnum {
  NewAndFeatures = 'New and Featured',
  ToolsAndEquipment = 'Tools and Equipment',
  VehicleRelatedSavings = 'Vehicle-related Savings',
  BusinessServices = 'Business Services',
  FreeHelpAndAdvice = 'Free Help and Advice',
  Personal = 'Personal'
}

const cardCategoriesArray = [
  CardCategoriesEnum.ToolsAndEquipment,
  CardCategoriesEnum.VehicleRelatedSavings,
  CardCategoriesEnum.BusinessServices,
  CardCategoriesEnum.FreeHelpAndAdvice,
  CardCategoriesEnum.Personal
];

export type Card = {
  category: CardCategoriesEnum;
  category2: CardCategoriesEnum;
  component: Component;
};

export enum TabId {
  Products = 'products',
  MembershipBenefits = 'membershipBenefits',
  Dashboard = 'dashboard',
  Connections = 'connections'
}

export type Tab = {
  id: string;
  title: string;
  cards: Card[];
};

type Config = {
  tabs?: Tab[];
  cards?: Card[];
};

enum SubscriptionType {
  'CHAS Advanced' = 'advanced',
  'CHAS Elite' = 'elite',
  'CHAS Standard' = 'standard',
  'CHAS Foundation' = 'foundation'
}

const configTypeMap = (countryKey: string, subscriptionName: string) =>
  !['US', 'AU'].includes(countryKey) ? SubscriptionType[subscriptionName] ?? 'default' : 'default';
const renderComponent = (
  key: Component,
  props: object,
  disableProductCard: boolean,
  purchasedProductCard: boolean,
  organizationId: string
) => {
  props = {
    ...props,
    organizationId
  };

  if (disableProductCard || purchasedProductCard) {
    props = {
      ...props,
      ...(disableProductCard && { disabled: true }),
      ...(purchasedProductCard && { purchased: true })
    };
  }
  const Component: React.FC<Config | {}> = componentMapper[key];

  if (Component) {
    return <Component {...props} />;
  }
  return null;
};

type RouteParams = {
  organizationId: string;
};

export const NewDashboardContainer: React.FC = () => {
  const classes = useStyles();
  const { organizationId } = useParams<RouteParams>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { hash } = useLocation();
  const { mainSubscription, isFetching } = useTypedSelector(state => state.mainSubscription);
  const { userInfo, isFetching: isFetchingUserInfo } = useTypedSelector(state => state.userInfo);
  const { organizationInfo } = useTypedSelector(state => state.organizationInfo);
  const { subscriptions } = useTypedSelector(state => state.subscriptionsV2);
  const initialTab = hash.slice(1) || '';
  const [activeTab, setActiveTab] = React.useState<string>(initialTab);
  const [displayDisclaimer, setDisplayDisclaimer] = React.useState(initialTab !== TabId.Dashboard);
  const [dashboardConfig, setDashboardConfig] = React.useState<Config>({});
  const membershipExpired = moment().isAfter(moment(mainSubscription.expirationDate as string));
  const usContractor = mainSubscription.countryISO2 === CountryIso2Code.US;
  const { organization } = useSelector((state: CommonRootState) => state.organization);
  // @ts-ignore
  const isActive = organization?.isActive || undefined;
  const vehicleComplianceSchemeID = 'vehicle-compliance-scheme';
  const [expandedRows, setExpandedRows] = useState(new Set(cardCategoriesArray));
  const { featureFlags } = useTypedSelector(state => state.featureFlags);
  const cards = React.useMemo(() => {
    if (dashboardConfig.tabs) {
      return dashboardConfig.tabs.find(({ id }, index) => (activeTab ? id === activeTab : index === 0))?.cards || [];
    }
    return dashboardConfig.cards || [];
  }, [dashboardConfig, activeTab]);
  const { hasGlobalPermission } = usePermission();
  const isFoundation = mainSubscription.description === SubscriptionTiers.Foundation;
  const proposeFoundation =
    userInfo &&
    !hasGlobalPermission(ActivityAction.Manage, ActivityResourceName.Organizations) &&
    !isFoundation &&
    mainSubscription.countryISO2 === 'GB' &&
    mainSubscription.expirationDate &&
    moment().isAfter(
      moment(mainSubscription.expirationDate)
        .add(3, 'months')
        .startOf('day')
    );

  const disableProductCard = (cardData: Partial<ProductCard>): boolean => {
    if (cardData.title === undefined || null) return false;
    if (cardData.id === vehicleComplianceSchemeID && !isActive) return true;

    return (
      !foundationProducts.includes(cardData.title as FoundationProducts) &&
      membershipExpired &&
      !activeTab.includes('dashboard')
    );
  };

  const isCardFeatureFlagged = (cardData: any): boolean => {
    if (!cardData.featureFlagId) return false;
    return mappedFeatureFlags(featureFlags)[cardData.featureFlagId];
  };

  const purchasedProductCard = (cardData: Partial<ProductCard>): boolean => {
    if (cardData.subscriptionTypePublicId === undefined || null) return false;

    return subscriptions?.some(
      ({ type, expirationDate }) => type === cardData.subscriptionTypePublicId && moment(expirationDate).isAfter()
    );
  };

  const getSortedCards = (cards: Card[]) => {
    const activeCards = [...cards]
      .filter(card => !disableProductCard(card as Partial<ProductCard>) && !(card as Partial<ProductCard>).disabled)
      .sort((a, b) => {
        return (
          Number(purchasedProductCard(a as Partial<ProductCard>)) -
          Number(purchasedProductCard(b as Partial<ProductCard>))
        );
      });

    const disabledCards = cards.filter(
      card => disableProductCard(card as Partial<ProductCard>) || (card as Partial<ProductCard>).disabled
    );
    return [...activeCards, ...disabledCards];
  };

  React.useEffect(() => {
    const { id, countryISO2, description } = mainSubscription;

    if (id) {
      const configUrl = `/organizations/static/configs/${countryISO2.toLowerCase()}-${configTypeMap(
        countryISO2,
        description
      )}.json`;

      fetch(configUrl)
        .then(response => response.json())
        .then(config => {
          setDashboardConfig(config);
        });
    }
  }, [mainSubscription]);

  React.useEffect(() => {
    organizationId && dispatch(fetchMainSubscription(organizationId));
  }, [organizationId, dispatch]);

  React.useEffect(() => {
    if (userInfo && organizationId && !usContractor) {
      const fetchOnlineTrainingData = () =>
        dispatch(
          fetchOnlineTrainingCredits({
            userId: userInfo.userId,
            organizationId
          })
        );

      const checkIfBusinessShieldNumberExists = () => {
        return organizationInfo?.organizationRegistrationNumbers.some(
          el => el.typeId === OrganizationRegistrationNumberType.BusinessShield
        );
      };

      if (checkIfBusinessShieldNumberExists()) {
        dispatch(fetchBusinessShieldSSOUrl({ userId: userInfo.userId, organizationId }));
        dispatch(fetchBusinessShieldCredits({ userId: userInfo.userId, organizationId }));
      }

      if (localStorage.getItem(ONLINE_TRAINING_STORAGE_KEY) === 'true') {
        batch(() => {
          dispatch(changeIsFetchingCredits());
          dispatch(changeIsFetchingSSOUrl());
        });
        setTimeout(() => {
          fetchOnlineTrainingData();
          localStorage.removeItem(ONLINE_TRAINING_STORAGE_KEY);
        }, 5000);
      } else {
        fetchOnlineTrainingData();
      }
    }
  }, [organizationId, userInfo, dispatch, usContractor, organizationInfo]);

  React.useEffect(() => {
    organizationId && dispatch(fetchOrganizationInfo(organizationId));
  }, [dispatch, organizationId]);

  React.useEffect(() => {
    setDisplayDisclaimer(activeTab !== TabId.Dashboard);
  }, [activeTab]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    history.replace({ hash: tab });
  };

  if (isFetching || isFetchingUserInfo) {
    return <Loading />;
  }

  if (proposeFoundation) {
    return <FoundationBanner organizationId={organizationId} subscriptionId={mainSubscription.id} />;
  }

  const handleNameClick = (category: string) => {
    setExpandedRows(prev => {
      const newExpandedRows = new Set(prev);

      if (newExpandedRows.has(category as CardCategoriesEnum)) {
        newExpandedRows.delete(category as CardCategoriesEnum);
      } else {
        newExpandedRows.add(category as CardCategoriesEnum);
      }
      return newExpandedRows;
    });
  };

  function featuredCards() {
    return (
      <Box className={classes.featuredCard}>
        <ResponsiveMasonry columnsCountBreakPoints={{ 320: 1, 1280: 3 }}>
          <Box className={classes.sectionTitle}>
            <Typography variant={'h5'}>New and Featured</Typography>
          </Box>
          <Masonry>
            {getSortedCards(cards)
              .filter(card => !!card.category2)
              .map(({ component, ...other }, index) => (
                <Box className={classes.card} key={index + other.category}>
                  {renderComponent(
                    component,
                    other,
                    disableProductCard(other),
                    purchasedProductCard(other),
                    organizationId
                  )}
                </Box>
              ))}
          </Masonry>
        </ResponsiveMasonry>
      </Box>
    );
  }

  return (
    <>
      <Box mt={2} pl={2} pr={2} className={classes.alerts}>
        <SubscriptionAlert organizationId={organizationId} subscription={mainSubscription} />
        <ExternalVerificationAlert />
      </Box>
      <DashboardSubscriptionDetails organizationId={organizationId} />
      {dashboardConfig.tabs && (
        <DashboardTabs tabs={dashboardConfig.tabs} activeTab={activeTab} onChange={handleTabChange} />
      )}
      {/*<Box className={classes.container}>*/}
      {activeTab === 'membershipBenefits' && (
        <ResponsiveMasonry columnsCountBreakPoints={{ 320: 1, 1280: 1 }}>
          <Masonry>
            {featuredCards()}
            {cardCategoriesArray.map(category => (
              <Box key={category} className={classes.cardContainer}>
                {/* Clickable Row */}
                <Box className={classes.name} onClick={() => handleNameClick(category)} style={{ cursor: 'pointer' }}>
                  <Box>
                    <Typography variant={'h5'}>{category}</Typography>
                  </Box>
                  <Box>{expandedRows.has(category) ? <ArrowDropDownIcon /> : <ArrowRightIcon />}</Box>
                </Box>
                <hr />
                <Collapse in={expandedRows.has(category)} timeout="auto" unmountOnExit>
                  <Box>
                    <ResponsiveMasonry columnsCountBreakPoints={{ 320: 1, 1280: 4 }}>
                      <Masonry>
                        {getSortedCards(cards)
                          .filter(card => card.category === category && card.component === 'Benefit')
                          .filter(({ ...other }) => !isCardFeatureFlagged(other))
                          .map(({ component, ...other }, index) => (
                            <Box className={classes.card} key={index + other.category}>
                              {renderComponent(
                                component,
                                other,
                                disableProductCard(other),
                                purchasedProductCard(other),
                                organizationId
                              )}
                            </Box>
                          ))}
                      </Masonry>
                    </ResponsiveMasonry>
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Masonry>
        </ResponsiveMasonry>
      )}
      {activeTab !== 'membershipBenefits' && (
        <ResponsiveMasonry columnsCountBreakPoints={{ 320: 1, 1280: 2 }}>
          <Masonry>
            {getSortedCards(cards)
              .filter(({ ...other }) => !isCardFeatureFlagged(other))
              .map(({ component, ...other }, index) => (
                <Box className={classes.card} key={index}>
                  {renderComponent(
                    component,
                    other,
                    disableProductCard(other),
                    purchasedProductCard(other),
                    organizationId
                  )}
                </Box>
              ))}
          </Masonry>
        </ResponsiveMasonry>
      )}
      {displayDisclaimer && activeTab !== '' && (
        <Box className={classes.disclaimer}>
          <p>{DISCLAIMER_NOTICE}</p>
        </Box>
      )}
      {/*</Box>*/}
      <OnlineTrainingDrawer />
    </>
  );
};
